import React, { useState, useEffect, useRef } from 'react';
import { Calendar, CheckSquare, Clock, Settings } from 'lucide-react';

const EmergencyExit = ({ children }) => {
  const [showCover, setShowCover] = useState(false);
  const lastTapTime = useRef(0);
  const inactivityTimeout = useRef(null);

  useEffect(() => {
    // Listen for Escape key as alternative trigger
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        activateCover();
      }
    };
    
    // Listen for custom emergency exit event from floating button
    const handleEmergencyEvent = () => {
      activateCover();
    };

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('trigger-emergency-exit', handleEmergencyEvent);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('trigger-emergency-exit', handleEmergencyEvent);
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
    };
  }, []);

  // Global double-tap handler for any element with data-emergency-trigger
  useEffect(() => {
    const handleClick = (e) => {
      const target = e.target.closest('[data-emergency-trigger]');
      if (target) {
        const currentTime = Date.now();
        const timeSinceLastTap = currentTime - lastTapTime.current;

        // Double-tap detected within 400ms
        if (timeSinceLastTap < 400 && timeSinceLastTap > 0) {
          e.preventDefault();
          e.stopPropagation();
          activateCover();
          lastTapTime.current = 0; // Reset
        } else {
          lastTapTime.current = currentTime;
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  const activateCover = () => {
    setShowCover(true);
    sessionStorage.setItem('emergency_cover_active', 'true');
    
    // Auto-revert after 60 seconds of inactivity
    inactivityTimeout.current = setTimeout(() => {
      deactivateCover();
    }, 60000);
  };

  const deactivateCover = () => {
    setShowCover(false);
    sessionStorage.removeItem('emergency_cover_active');
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }
  };

  // Check if cover was active on mount
  useEffect(() => {
    if (sessionStorage.getItem('emergency_cover_active') === 'true') {
      setShowCover(true);
    }
  }, []);

  // Neutral Daily Focus Cover Screen
  const CoverScreen = () => {
    const today = new Date();
    const timeStr = today.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });

    return (
      <div className="fixed inset-0 z-[9999] bg-[#F5F5F0]">
        {/* Neutral Daily Focus Interface */}
        <div className="max-w-4xl mx-auto px-6 py-8 min-h-screen">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b-2 border-gray-300">
            <div className="flex items-center gap-3">
              <Calendar className="w-7 h-7 text-gray-600" />
              <h1 className="text-2xl font-normal text-gray-800">Daily Focus</h1>
            </div>
            <button
              onClick={deactivateCover}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              title="Return"
            >
              <Settings className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="text-right text-gray-500 text-sm mb-6">
            Last updated: {timeStr}
          </div>

          {/* Morning Section */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
              🌤 Morning
            </h2>
            <div className="space-y-2">
              {[
                'Drink water',
                'Breathe for 60 seconds',
                'Stretch your body'
              ].map((task, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded border border-gray-200">
                  <input type="checkbox" className="w-4 h-4" defaultChecked={index === 0} />
                  <span className="text-gray-700 text-sm">{task}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Afternoon Section */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
              🌿 Afternoon
            </h2>
            <div className="space-y-2">
              {[
                'Go for a short walk',
                'Do something kind for yourself'
              ].map((task, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded border border-gray-200">
                  <input type="checkbox" className="w-4 h-4" />
                  <span className="text-gray-700 text-sm">{task}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Evening Section */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-700 mb-3 flex items-center gap-2">
              🌙 Evening
            </h2>
            <div className="space-y-2">
              {[
                'Reflect on one small win today'
              ].map((task, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded border border-gray-200">
                  <input type="checkbox" className="w-4 h-4" />
                  <span className="text-gray-700 text-sm">{task}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {showCover && <CoverScreen />}
      {children}
    </>
  );
};

export default EmergencyExit;
